import type { SearchFacets, SearchHit } from '@contracts';
import { ArrowRight, Check, CircleDashed, CircleHelp, List, Loader2, MessageSquareDashed, Radio, ShieldCheck, Sparkles, TriangleAlert, WifiOff } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { type BriefPoint, buildBrief, siteContext, type Tone } from '../ask/brief';
import { applyInterpretation, interpret, type Interpretation } from '../ask/interpret';
import { FilterBar } from '../filters/FilterBar';
import { clock } from '../lib/time';
import { deviceSummary } from '../records/DeviceDetails';
import { recordPath } from '../records/paths';
import { Cite, RecordTable } from '../records/RecordTable';
import { useScope } from '../scope/scope';
import { useCatalog, useEvidence, useFacets, useRecord } from '../search/hooks';
import { emptyFilters, type FilterState, parseFilters, toSearchQuery, uuid, writeFilters } from '../search/state';
import { Button, buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { Card, Skeleton, Spark, useNow } from '../ui/misc';
import { PageHeader } from './PageHeader';

// On this page `q` is the question; a literal text filter travels as `text`.
const FILTER_KEYS = ['org', 'site', 'os', 'status', 'seen', 'kind', 'sort', 'text', 'duplicateName'];
const EXAMPLES: Array<[string, string[]]> = [
  ['Triage', ['What needs a look?', 'Which customers have offline devices?', 'Devices reporting attention, worst first']],
  ['Check-ins', ['Devices that haven’t checked in this week', 'Never reported', 'Macs not seen in 3 days']],
  ['Customers', ['Offline devices at Harbor Dental', 'Windows devices at Acme', 'Linux devices with problems']],
];

export function AskPage() {
  const { mspId } = useScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q')?.trim() ?? '';
  // A present but empty or malformed device is a damaged link, not a request about every device.
  const badDevice = params.has('device') && !uuid.test(params.get('device') ?? '');
  const deviceId = badDevice ? null : params.get('device');
  const explicit = FILTER_KEYS.some(k => params.has(k));
  const { catalog, isPending: catalogPending, isError: catalogFailed, error: catalogError, refetch: retryCatalog } = useCatalog();
  // Without the customer and site names, "at Acme" can't be read, and dropping it would widen the brief.
  const reading = useMemo(() => (q && !catalogPending && !catalogFailed ? interpret(q, catalog) : null), [q, catalog, catalogPending, catalogFailed]);
  const device = useRecord('device', deviceId ?? undefined);

  const linked = useMemo(() => {
    if (deviceId || badDevice || !explicit) return null;
    const p = new URLSearchParams(params);
    const text = p.get('text');
    p.delete('q'); p.delete('text'); p.delete('device');
    if (text) p.set('q', text);
    return parseFilters(p);
  }, [deviceId, badDevice, explicit, params]);
  // A brief from a damaged link would silently cover more than it asked for, so refuse it.
  const invalid = badDevice ? ['device'] : linked?.invalid ?? [];
  const filters: FilterState | null = useMemo(() => {
    if (badDevice) return null;
    if (deviceId) return device.data?.siteId ? { ...emptyFilters, kind: 'device', site: [device.data.siteId] } : null;
    if (linked) return linked.invalid.length ? null : linked.state;
    if (reading?.understood) return applyInterpretation(emptyFilters, reading);
    return null;
  }, [badDevice, deviceId, device.data, linked, reading]);

  const now = useNow(60_000);
  const evidenceQuery = useMemo(() => filters ? toSearchQuery({ ...filters, page: 1, size: 100,
    sort: filters.sort ?? (filters.kind === 'device' || filters.status.length || filters.os.length || filters.seen ? { field: 'status', direction: 'asc' } : null) }) : null,
  [filters]);
  const evidence = useEvidence(evidenceQuery);
  const facets = useFacets(evidenceQuery ? { ...evidenceQuery, sort: undefined, limit: 25 } : null);

  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  const ask = (question: string) => navigate(`/w/${mspId}/ask?${new URLSearchParams({ q: question })}`);
  const submit = (e: FormEvent) => { e.preventDefault(); if (draft.trim()) ask(draft.trim()); };
  const edit = (next: FilterState) => {
    const out = writeFilters({ ...next, page: 1, size: 25 });
    const text = out.get('q');
    out.delete('q');
    if (text) out.set('text', text);
    if (!out.has('kind')) out.set('kind', next.kind);
    if (q) out.set('q', q);
    setParams(out);
  };

  const hits = evidence.data?.result.hits ?? [];
  const citations = useMemo(() => new Map(hits.map((h, i) => [h.id, i + 1])), [hits]);
  const lead = deviceId && device.data && evidence.data && facets.data ? deviceLead(device.data, hits, facets.data, now) : null;
  const brief = evidence.data && facets.data && !facets.isPlaceholderData ? buildBrief({ hits, facets: facets.data, groupBy: reading?.groupBy, now,
    // The device lead already says whether the site shares the problem.
    ...(lead && device.data ? { subject: `at ${device.data.organizationName} · ${device.data.siteName}`, skipClusters: true } : {}) }) : null;
  const [hover, setHover] = useState<string | null>(null);
  const deviceOnly = evidenceQuery?.kinds?.length === 1 && evidenceQuery.kinds[0] === 'device';
  const listLink = filters ? `/w/${mspId}/${deviceOnly ? 'devices' : 'search'}?${writeFilters({ ...filters, page: 1, size: 25 }, deviceOnly ? { kind: 'device' } : {})}` : '';
  const stage = !filters ? 0 : evidence.isPending || facets.isPending ? 1 : 2;

  return (
    <div className="mx-auto max-w-[1100px] space-y-5">
      <PageHeader eyebrow={<span className="inline-flex items-center gap-1.5"><Spark className="size-3.5" />Ask</span>}
        title={q || (filters ? 'Brief' : 'Ask about your fleet')}
        description={!q && !filters ? 'Questions become filters you can see and edit. Briefs cite the records they’re based on.' : undefined} />

      <form onSubmit={submit} className="flex items-center gap-2 rounded-2xl border border-line bg-panel p-1.5 pl-4 shadow-card focus-within:border-brand/50 focus-within:ring-4 focus-within:ring-brand/10">
        <Spark className="size-[18px] shrink-0" />
        <input value={draft} onChange={e => setDraft(e.target.value)} aria-label="Question" data-page-search
          placeholder="e.g. “Which Harbor Dental devices haven’t checked in this week?”"
          className="h-10 min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-3" />
        {draft.trim() && draft.trim() !== q && <Button type="submit" variant="brand" className="animate-fade">Ask <ArrowRight className="size-4" /></Button>}
      </form>

      {!q && !filters && !deviceId && !invalid.length && <Landing onAsk={ask} />}

      {invalid.length > 0 && (
        <Card className="p-5"><div role="alert">
          <p className="font-medium">This link has filters that aren’t valid ({invalid.join(', ')}).</p>
          <p className="mt-1 text-[13.5px] text-ink-2">I haven’t written a brief: without those filters it would cover more customers or devices than the link asked for.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {q && <Button variant="brand" size="sm" onClick={() => ask(q)}>Read the question again</Button>}
            {linked && <Button size="sm" onClick={() => edit(linked.state)}>Continue without them</Button>}
          </div>
        </div></Card>
      )}

      {(q || deviceId) && catalogPending && <Card className="p-5"><Skeleton className="w-1/3" /></Card>}
      {q && !explicit && !deviceId && catalogFailed && <Card><ErrorPanel error={catalogError} onRetry={() => void retryCatalog()} /></Card>}

      {deviceId && device.isError && <Card><ErrorPanel error={device.error} onRetry={() => void device.refetch()} /></Card>}
      {deviceId && device.isPending && !catalogPending && <Card className="p-5"><Skeleton className="w-1/3" /></Card>}

      {reading && !reading.understood && !explicit && !deviceId && !invalid.length && <NotUnderstood reading={reading} />}

      {filters && (
        <>
          <Steps stage={stage} />
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[13px] font-semibold text-ink-2">{deviceId ? 'Looking at' : 'How I read this'}</h2>
              <span className="flex-1" />
              {listLink && <Link to={listLink} className={buttonClass('ghost', 'sm')}><List className="size-3.5" />Open as list</Link>}
            </div>
            {deviceId && device.data && (
              <p className="mt-2 text-[13.5px] text-ink-2">
                <Link to={recordPath(mspId, 'device', device.data.id)} className="font-mono font-semibold text-ink hover:underline">{device.data.title}</Link> and the other devices at {device.data.organizationName} · {device.data.siteName}, to tell a device problem from a site problem.
              </p>
            )}
            {reading && reading.readings.length > 0 && !deviceId && (
              <ul className="mt-2 space-y-1">
                {reading.readings.map((r, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-[13.5px]">
                    <span className="rounded bg-sunken px-1.5 py-px font-medium text-ink">“{r.text}”</span>
                    <ArrowRight className="size-3.5 text-ink-3" /><span className="text-ink-2">{r.meaning}</span>
                  </li>
                ))}
              </ul>
            )}
            {reading && reading.skipped.length > 0 && !deviceId && (
              <ul className="mt-2 space-y-1">
                {reading.skipped.map((s, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-[13px] text-ink-3">
                    <span className="rounded border border-dashed border-line-strong px-1.5 py-px">“{s.text}”</span>{s.reason}
                  </li>
                ))}
              </ul>
            )}
            {!deviceId && (
              <div className="mt-3 border-t border-line pt-3">
                <FilterBar state={filters} onChange={edit} facets={facets.data} catalog={catalog}
                  fields={filters.kind === 'organization' ? [] : ['org', 'site', 'status', 'signal', 'os', 'seen', 'software', 'duplicateName']} />
              </div>
            )}
          </Card>

          {evidence.isError || facets.isError ? <Card><ErrorPanel error={evidence.error ?? facets.error} onRetry={() => { void evidence.refetch(); void facets.refetch(); }} /></Card> : (
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 border-b border-line bg-gradient-to-r from-brand-soft/70 to-transparent px-5 py-3">
                <Sparkles className="size-4 text-brand" />
                <h2 className="font-semibold">Brief</h2>
                <span className="rounded-full border border-line bg-panel px-2 py-px text-[11.5px] text-ink-3" title="Built-in rules summarise the retrieved records. No AI model is connected, and no record content leaves the platform.">
                  Computed from records · no AI model
                </span>
              </div>
              <div className="px-5 py-4">
                {!brief ? <div className="space-y-3"><Skeleton className="h-5 w-2/3" /><Skeleton className="w-full" /><Skeleton className="w-5/6" /></div> : (
                  <>
                    {lead && <div className="mb-4 space-y-2 border-b border-line pb-4">{lead.map(p => <Point key={p.key} point={p} citations={citations} onHover={setHover} />)}</div>}
                    <p className="text-[17px] font-semibold leading-snug">{brief.headline}</p>
                    <ul className="mt-3 space-y-2.5">{brief.points.map(p => <Point key={p.key} point={p} citations={citations} onHover={setHover} />)}</ul>
                    {brief.coverage && <p className="mt-3 text-[12.5px] text-ink-3">{brief.coverage}</p>}
                  </>
                )}
              </div>
              {evidence.data && (
                <div className="flex flex-wrap items-start gap-x-6 gap-y-2 border-t border-line bg-sunken/40 px-5 py-3 text-[12px] text-ink-3">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="size-3.5" />Read-only · retrieved {clock(evidence.data.retrievedAt)} under your access</span>
                  <span>Record text is treated as data, never as instructions.</span>
                  <ul className="w-full list-disc space-y-0.5 pl-4">{evidence.data.limitations.map(l => <li key={l}>{l}</li>)}</ul>
                </div>
              )}
            </Card>
          )}

          {evidence.data && hits.length > 0 && (
            <section aria-labelledby="evidence" className="space-y-2">
              <div className="flex items-baseline gap-2 px-1">
                <h2 id="evidence" className="text-[15px] font-semibold">Evidence</h2>
                <span className="text-[13px] text-ink-3">{hits.length < evidence.data.result.total ? `first ${hits.length} of ${evidence.data.result.total}` : `${hits.length} record${hits.length === 1 ? '' : 's'}`}, numbered as cited</span>
              </div>
              <Card className="overflow-hidden">
                <RecordTable hits={hits} mode={deviceOnly ? 'device' : 'mixed'} loading={false} sort={null}
                  defaultSort={{ field: 'status', direction: 'asc' }} citations={citations} selectedId={hover} caption="Evidence records" />
              </Card>
            </section>
          )}
        </>
      )}
    </div>
  );
}

const toneIcon: Record<Tone, { icon: typeof Check; className: string }> = {
  neutral: { icon: CircleHelp, className: 'text-ink-3' }, attention: { icon: TriangleAlert, className: 'text-attention' },
  offline: { icon: WifiOff, className: 'text-offline' }, unknown: { icon: CircleDashed, className: 'text-unknown' },
  pattern: { icon: Radio, className: 'text-brand' },
};

function Point({ point, citations, onHover }: { point: BriefPoint; citations: Map<string, number>; onHover: (id: string | null) => void }) {
  const { icon: Icon, className } = toneIcon[point.tone];
  const jump = (id: string) => {
    // The table and the phone list both carry the record; use the one on screen.
    const rows = [...document.querySelectorAll<HTMLElement>(`[data-record="${id}"]`)];
    const row = rows.find(el => el.getClientRects().length > 0) ?? rows[0];
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row?.focus({ preventScroll: true });
  };
  return (
    <li className={cn('flex gap-3 text-[14px] leading-relaxed', point.tone === 'pattern' && 'rounded-lg bg-brand-soft/50 px-3 py-2')}>
      <Icon className={cn('mt-1 size-4 shrink-0', className)} />
      <span>
        {point.tone === 'pattern' && <span className="mr-1.5 text-[12px] font-semibold uppercase tracking-wide text-brand-ink">Possible pattern</span>}
        {point.text}
        {point.cites.length > 0 && (
          <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
            {point.cites.map(id => (
              <button key={id} type="button" onClick={() => jump(id)} aria-label={`Show cited record ${citations.get(id)}`}
                onMouseEnter={() => onHover(id)} onMouseLeave={() => onHover(null)} onFocus={() => onHover(id)} onBlur={() => onHover(null)}>
                <Cite n={citations.get(id)} />
              </button>
            ))}
          </span>
        )}
      </span>
    </li>
  );
}

function Steps({ stage }: { stage: number }) {
  const steps = ['Read the question', 'Gather scoped evidence', 'Brief'];
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]" aria-label="Progress">
      {steps.map((s, i) => (
        <li key={s} className={cn('inline-flex items-center gap-1.5', i < stage ? 'text-ink-2' : i === stage ? 'text-ink' : 'text-ink-3')}>
          {i < stage || stage === 2 ? <Check className="size-3.5 text-healthy" /> : i === stage ? <Loader2 className="size-3.5 animate-spin text-brand" /> : <span className="size-3.5 rounded-full border border-line-strong" />}
          {s}{i < steps.length - 1 && <span className="ml-1 text-ink-3">→</span>}
        </li>
      ))}
    </ol>
  );
}

function Landing({ onAsk }: { onAsk: (q: string) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {EXAMPLES.map(([group, questions]) => (
        <Card key={group} className="p-4">
          <h2 className="text-[12.5px] font-semibold uppercase tracking-wide text-ink-3">{group}</h2>
          <ul className="mt-2 space-y-1">
            {questions.map(q => (
              <li key={q}><button type="button" onClick={() => onAsk(q)} className="w-full rounded-lg px-2 py-1.5 text-left text-ink-2 transition-colors hover:bg-hover hover:text-ink">{q}</button></li>
            ))}
          </ul>
        </Card>
      ))}
      <Card className="p-4 md:col-span-3">
        <p className="text-[13px] leading-relaxed text-ink-2">
          <span className="font-medium text-ink">What I can answer today:</span> device status, operating system, check-in times, customers and sites — anything in inventory you can access.
          {' '}Tickets, security events, installed software and patches aren’t connected yet, so I’ll say so instead of guessing.
        </p>
      </Card>
    </div>
  );
}

function NotUnderstood({ reading }: { reading: Interpretation }) {
  const { mspId } = useScope();
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <MessageSquareDashed className="mt-0.5 size-5 text-ink-3" />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">I couldn’t turn that into a search yet</h2>
          <p className="mt-1 text-ink-2">I can read status, operating system, check-in times, customers and sites. Nothing else in the question matched those.</p>
          {reading.skipped.length > 0 && (
            <ul className="mt-3 space-y-1">{reading.skipped.map((s, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-[13px] text-ink-3"><span className="rounded border border-dashed border-line-strong px-1.5 py-px">“{s.text}”</span>{s.reason}</li>
            ))}</ul>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/w/${mspId}/search?${new URLSearchParams({ q: reading.input })}`} className={buttonClass('secondary', 'sm')}>Search inventory for the words</Link>
            {reading.filters.q && <Link to={`/w/${mspId}/search?${new URLSearchParams({ q: reading.filters.q })}`} className={buttonClass('secondary', 'sm')}>Search for “{reading.filters.q}”</Link>}
          </div>
        </div>
      </div>
    </Card>
  );
}

/** For "Investigate <device>": is this the device, or its site? */
function deviceLead(device: SearchHit, siteHits: SearchHit[], siteFacets: SearchFacets, now: number): BriefPoint[] {
  const summary = deviceSummary(device, now);
  const points: BriefPoint[] = [{ key: 'device', tone: summary.tone === 'warn' ? 'attention' : 'neutral', text: summary.text, cites: [device.id] }];
  points.push(siteContext(device, siteHits, siteFacets.total, now));
  return points;
}

